// R114 #6 (Jackie: "we need a way to mark it as 'couldn't resolve' or
// 'blocked by owner' — the owner said no to fans / a portable AC and the only
// button was Mark smoothed"). Guest Recovery outcomes + an append-only action
// log. Pure ESM, no DOM, no I/O: the Vite client imports it straight from
// GuestRecoveryPage / IssuesPage / StaysCalendarPage / QuoInboxPage /
// OwnerProfilePage, `node lib/guestRecovery.test.mjs` tests the SAME code, and
// server/assetops.js (CommonJS) loads it with a memoized dynamic import() —
// one source of truth for the rules on both sides.
//
// NAMING DEBT — read before touching recovery fields:
//   recovery_status 'smoothed' = CLOSED (legacy name). It stays a binary
//   'open' | 'smoothed' because every existing reader (board resolve, merge
//   target, 30-day window, sync keep rule, Calendar / Quo / Issues chips) uses
//   it only as a closed/open test. HOW a case was closed is recovery_outcome:
//   'smoothed' | 'partial' | 'unresolved' | 'blocked_owner' (null while open;
//   a legacy closed row with no outcome reads as 'smoothed' — it was).
//   smoothed_at = the CLOSED-at stamp for any outcome (re-stamped when the
//   outcome changes). Read state through outcomeOf() / stateOf(), never by
//   comparing recovery_status to 'smoothed' to mean "the guest is happy".
//
// Fields on BOTH stores (ao_message_tickets rows and ao_guest_recovery grc_
// cases): recovery_status, recovery_outcome, recovery_log, recovery_note (a
// back-compat mirror of the latest logged text), recovery_by / recovery_at
// (last touched), smoothed_at. recovery_log entries are
// { id, at, by, by_name, kind: 'legacy'|'action'|'close'|'reopen', outcome?,
//   prev_outcome?, text, source? } — append-only (corrections = log again).

export const OUTCOMES = ['smoothed', 'partial', 'unresolved', 'blocked_owner'];
export const OUTCOME_LABEL = {
  smoothed: 'Smoothed',
  partial: 'Partly resolved',
  unresolved: "Couldn't resolve",
  blocked_owner: 'Blocked by owner',
};
export const OUTCOME_HELP = {
  smoothed: 'guest happy',
  partial: 'helped, but not fully',
  unresolved: 'guest still unhappy',
  blocked_owner: 'owner declined the fix, e.g. fans / portable AC',
};
export const OUTCOME_ICON = { smoothed: '✓', partial: '◐', unresolved: '✕', blocked_owner: '⊘' };
export const STATE_LABEL = { open: 'Open', ...OUTCOME_LABEL };
// existing main.css .pill-* classes only — no new colours
export const STATE_PILL = { open: 'pill-red', smoothed: 'pill-green', partial: 'pill-amber', unresolved: 'pill-gray', blocked_owner: 'pill-purple' };
// the page's tabs, in order
export const FILTERS = ['open', 'smoothed', 'partial', 'unresolved', 'blocked_owner', 'all'];
// what GET ?state= accepts ('closed' = any outcome)
export const STATE_QUERY = ['open', 'smoothed', 'partial', 'unresolved', 'blocked_owner', 'closed', 'all'];

export const NOTE_MAX = 1000;
export const LOG_MAX = 100;
export const CLOSED_WINDOW_DAYS = 30;
export const LIST_CAP = 150;
export const LIST_CAP_FILTERED = 500;
export const RECOVERY_FIELDS = ['recovery_status', 'recovery_outcome', 'recovery_note', 'recovery_log', 'recovery_by', 'recovery_at', 'smoothed_at'];
export const NOTE_REQUIRED_ERROR = "A note is required for Partly resolved / Couldn't resolve / Blocked by owner — what did we do, and why couldn't we fix it?";

const DAY = 86400000;
const str = (v) => (v == null ? '' : String(v));
const clip = (v, n) => str(v).trim().slice(0, n);
export const cleanText = (v) => clip(v, NOTE_MAX);

// Hostaway stamps are UTC "YYYY-MM-DD HH:mm:ss" with no zone; board cases are
// ISO. Normalize so Safari parses them and Chrome doesn't read them as local.
export function parseTs(s) {
  const x = str(s).trim();
  if (!x) return 0;
  const t = Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(x) ? x.replace(' ', 'T') + 'Z' : x);
  return Number.isFinite(t) ? t : 0;
}

export const noteRequired = (o) => o !== 'smoothed';
export const isClosed = (r) => !!r && r.recovery_status === 'smoothed';
export function outcomeOf(r) {
  if (!isClosed(r)) return null;
  return OUTCOMES.includes(r.recovery_outcome) ? r.recovery_outcome : 'smoothed';
}
export const stateOf = (r) => outcomeOf(r) || 'open';
export const closedAt = (r) => (isClosed(r) ? (r.smoothed_at || null) : null);
export const outcomeLabel = (o) => OUTCOME_LABEL[o] || OUTCOME_LABEL.smoothed;
// "Open" / "Smoothed" / "Blocked by owner"… for any row-ish {recovery_status, recovery_outcome}
export const stateLabel = (r) => STATE_LABEL[stateOf(r)];

// The log, never mutating r. A row that already has a log gets a copy; an
// older row gets one synthesized from its legacy fields so the timeline is
// never empty: the note as a 'legacy' entry, plus a 'close' entry when it is
// closed. Ids are derived from the record id so two records' synthesized
// entries never collide when a case takes over a ticket (mergeLogs).
//
// R114 fix (review recovery#2): an old (pre-R114) tab's autosave writes ONLY
// the recovery_note mirror. On a row that already has a log, that text is in
// no entry — and the next op re-derives the mirror from the log, which used
// to erase it for good. So a recovery_note that matches no logged text is
// surfaced here as one 'legacy' entry (source 'legacy-autosave'): every
// reader sees it, and the next op (which persists migrateLog) keeps it in
// order. Derived, not stored per keystroke — no coalescing, the log stays
// append-only. The old textarea always resent the whole note (starting with
// the mirror it was opened with), so a logged prefix is dropped.
export function migrateLog(r) {
  if (!r) return [];
  const rid = r.id ? `:${r.id}` : '';
  if (Array.isArray(r.recovery_log) && r.recovery_log.length) {
    const log = r.recovery_log.filter(Boolean).map(e => ({ ...e }));
    const pending = str(r.recovery_note).trim().slice(0, NOTE_MAX);
    if (pending) {
      const fresh = stripLoggedPrefix(pending, log);
      const known = new Set(log.map(e => str(e.text).trim()).filter(Boolean));
      if (fresh && !known.has(pending) && !known.has(fresh)) {
        log.push({ id: `legacy-note${rid}:${str(r.recovery_at)}`, kind: 'legacy', source: 'legacy-autosave', at: r.recovery_at || null, by: str(r.recovery_by), by_name: '', text: fresh });
      }
    }
    return log;
  }
  const out = [];
  const note = str(r.recovery_note).trim();
  if (note) out.push({ id: `legacy${rid}`, kind: 'legacy', at: r.recovery_at || r.created_at || null, by: str(r.recovery_by), by_name: '', text: note.slice(0, NOTE_MAX) });
  if (isClosed(r)) out.push({ id: `legacy-close${rid}`, kind: 'close', outcome: outcomeOf(r), at: r.smoothed_at || r.recovery_at || null, by: str(r.recovery_by), by_name: '', text: '' });
  return out;
}

let seq = 0;
export function newEntryId(atMs) {
  seq = (seq + 1) % 1296;
  return `rl_${Math.max(0, Number(atMs) || Date.now()).toString(36)}${seq.toString(36).padStart(2, '0')}${Math.random().toString(36).slice(2, 6)}`;
}

// The latest non-empty logged text — what back-compat readers of
// recovery_note (stays detail, the sync keep rule, board merges) see.
function mirrorNote(rec) {
  const log = Array.isArray(rec.recovery_log) ? rec.recovery_log : [];
  for (let i = log.length - 1; i >= 0; i--) { const t = str(log[i] && log[i].text).trim(); if (t) return t.slice(0, NOTE_MAX); }
  return str(rec.recovery_note);
}
// An old (R77) textarea resends the WHOLE note: the mirror it was opened with
// + what was typed since. Drop the longest already-logged text it starts
// with, so only the new words become an entry ('' = nothing new).
// R114 fix2 (rec2): only a prefix that ends on a word edge — a logged "Call"
// must never cut "Called owner, refused" into "ed owner, refused".
const WORD_CH = /[\p{L}\p{M}\p{N}]/u;
const atWordEdge = (t, i) => i >= t.length || !WORD_CH.test(t[i]) || !WORD_CH.test(t[i - 1]);
function stripLoggedPrefix(text, log) {
  const t = str(text).trim();
  let best = '';
  for (const e of Array.isArray(log) ? log : []) {
    const p = str(e && e.text).trim();
    if (p && p.length > best.length && t.startsWith(p) && atWordEdge(t, p.length)) best = p;
  }
  return best ? t.slice(best.length).replace(/^[\s,;:.\-\u2013\u2014]+/u, '').trim() : t;
}

// Apply ONE recovery operation to a ticket or case record (mutates rec).
//   op = { op:'log', text, source? }                  append an action; never closes (works on closed cases too)
//      | { op:'close', outcome, text?, source? }      close with an outcome; a non-smoothed outcome needs text
//      | { op:'reopen', text?, source? }              back to open, outcome cleared, history kept
//      | { op:'note', note }                          LEGACY (an old tab's autosave): mirror only, no entry
//   ctx = { by, by_name, at (ISO), id? }
// → { ok, changed, error?, entry? }. Validation happens before any mutation:
// a refused op leaves rec untouched. changed:false = nothing to write.
export function applyRecoveryOp(rec, op, ctx = {}) {
  if (!rec || !op) return { ok: false, changed: false, error: 'bad_op' };
  const kind = op.op;
  const at = ctx.at || new Date().toISOString();
  const by = clip(ctx.by, 80);
  const byName = clip(ctx.by_name, 80);
  if (kind === 'note') {
    const t = cleanText(op.note != null ? op.note : op.text);
    if (str(rec.recovery_note) === t && rec.recovery_status) return { ok: true, changed: false };
    rec.recovery_note = t;
    if (!rec.recovery_status) rec.recovery_status = 'open';
    if (by || !rec.recovery_by) rec.recovery_by = by || str(rec.recovery_by);
    rec.recovery_at = at;
    return { ok: true, changed: true };
  }
  if (!['log', 'close', 'reopen'].includes(kind)) return { ok: false, changed: false, error: 'bad_op' };
  const text = cleanText(op.text);
  const closed = isClosed(rec);
  const prev = outcomeOf(rec);
  let entry;
  if (kind === 'log') {
    if (!text) return { ok: false, changed: false, error: 'text_required' };
    entry = { kind: 'action', text };
  } else if (kind === 'close') {
    const outcome = op.outcome;
    if (!OUTCOMES.includes(outcome)) return { ok: false, changed: false, error: 'bad_outcome' };
    // a double click / re-send of the outcome it already has: idempotent —
    // checked BEFORE the note rule (the note was given on the first close)
    if (closed && prev === outcome && !text) return { ok: true, changed: false };
    if (noteRequired(outcome) && !text) return { ok: false, changed: false, error: 'note_required' };
    if (closed && prev === outcome) {
      entry = { kind: 'action', text };                       // same outcome + more words = just a log line
    } else {
      entry = { kind: 'close', outcome, text, ...(closed ? { prev_outcome: prev } : {}) };
    }
  } else {   // reopen
    if (!closed) {
      if (!text) return { ok: true, changed: false };
      entry = { kind: 'action', text };
    } else {
      entry = { kind: 'reopen', text, prev_outcome: prev };
    }
  }
  // persist the lazy migration first, so the legacy note keeps its place
  rec.recovery_log = migrateLog(rec);
  if (entry.kind === 'close') {
    rec.recovery_status = 'smoothed';
    rec.recovery_outcome = entry.outcome;
    rec.smoothed_at = at;                                     // open→closed or an outcome change (no-ops returned above)
  } else if (entry.kind === 'reopen') {
    rec.recovery_status = 'open';
    rec.recovery_outcome = null;
  } else if (!rec.recovery_status) {
    rec.recovery_status = 'open';
  }
  if (!closed && entry.kind !== 'close' && rec.recovery_outcome === undefined) rec.recovery_outcome = null;
  const full = { id: ctx.id || newEntryId(Date.parse(at)), at, by, by_name: byName, ...entry, ...(op.source ? { source: clip(op.source, 40) } : {}) };
  rec.recovery_log.push(full);
  if (rec.recovery_log.length > LOG_MAX) rec.recovery_log = rec.recovery_log.slice(-LOG_MAX);
  rec.recovery_note = mirrorNote(rec);
  rec.recovery_by = by || str(rec.recovery_by);
  rec.recovery_at = at;
  return { ok: true, changed: true, entry: full };
}

// Old clients post { status?, note? } (R57/R77). Map them onto ops; rec lets
// a full-note resend (the old textarea always sent the whole note) avoid
// repeating the note as a second entry. null = invalid body.
export function legacyBodyToOp(body, rec) {
  const b = body || {};
  const status = b.status != null ? String(b.status) : null;
  const note = b.note != null ? cleanText(b.note) : null;
  // a different note (its autosave never landed) keeps only the words that
  // aren't logged yet — the old textarea starts with the mirror it opened with
  const fresh = note && (!rec || note !== str(rec.recovery_note).trim()) ? (rec ? stripLoggedPrefix(note, migrateLog(rec)) : note) : '';
  if (status === 'smoothed') return { op: 'close', outcome: 'smoothed', text: fresh };
  if (status === 'open') return { op: 'reopen', text: fresh };
  if (status != null) return null;
  if (note != null) return { op: 'note', note };
  return null;
}

// R114 fix (review recovery#5): a case that takes over a CLOSED guest thread
// is (or stays) OPEN, but the history it inherits ends on that close —
// append a reopen marker (the thread's outcome as prev_outcome) so the
// timeline never reads "closed" on an open case. Never touches the record's
// status / outcome. Mutates rec; returns the entry.
export function reopenMarker(rec, prevOutcome, op = {}, ctx = {}) {
  if (!rec) return null;
  const at = ctx.at || new Date().toISOString();
  const by = clip(ctx.by, 80);
  rec.recovery_log = migrateLog(rec);
  const entry = { id: ctx.id || newEntryId(Date.parse(at)), at, by, by_name: clip(ctx.by_name, 80), kind: 'reopen', prev_outcome: OUTCOMES.includes(prevOutcome) ? prevOutcome : 'smoothed', text: cleanText(op.text), ...(op.source ? { source: clip(op.source, 40) } : {}) };
  rec.recovery_log.push(entry);
  if (rec.recovery_log.length > LOG_MAX) rec.recovery_log = rec.recovery_log.slice(-LOG_MAX);
  rec.recovery_note = mirrorNote(rec);
  rec.recovery_by = by || str(rec.recovery_by);
  rec.recovery_at = at;
  return entry;
}

// Who may open the Guest Recovery list (the server's RECOVERY_ROLES gate on
// GET/POST /issues/guest-recovery* — same set; scripts/test_guest_recovery.js
// checks the two agree). R114 fix (review recovery#3): a field role must
// never get a link into a page that answers it 403.
export const RECOVERY_ROLES = ['admin', 'director', 'manager', 'remote_ops'];
export const canOpenRecovery = (role) => RECOVERY_ROLES.includes(str(role));
// R114 fix2 (rec2): which 403 the list answered. 'role' = the office-only gate
// ("Forbidden — requires role: …", the accounting deny) → the friendly lock.
// 'unverified' = server.js's fail-closed "role could not be verified" (a cold
// role cache + a Supabase blip) or a "role check failed, try again" → a retry
// message: a blip must never tell an office user the page isn't theirs.
export function forbiddenKind(body) {
  const e = str(body && (body.error || body.message));
  return /could not be verified|check failed|try again/i.test(e) ? 'unverified' : 'role';
}

// The recovery fields present on src (log copied — never aliased).
export function pickRecovery(src) {
  const out = {};
  if (!src) return out;
  for (const k of RECOVERY_FIELDS) if (src[k] !== undefined) out[k] = k === 'recovery_log' && Array.isArray(src[k]) ? src[k].map(e => ({ ...e })) : src[k];
  return out;
}
// The hourly sync rebuilds a ticket from the classifier — copy the
// human-owned recovery fields from the fresh store record onto it EXACTLY
// (fields missing on src are removed), keeping the old smoothed_at fallback.
export function carryRecovery(dst, src) {
  const keepStamp = dst.smoothed_at;
  for (const k of RECOVERY_FIELDS) {
    if (src && src[k] !== undefined) dst[k] = k === 'recovery_log' && Array.isArray(src[k]) ? src[k].map(e => ({ ...e })) : src[k];
    else delete dst[k];
  }
  dst.recovery_note = str(dst.recovery_note);
  dst.recovery_by = str(dst.recovery_by);
  dst.recovery_at = dst.recovery_at || null;
  dst.smoothed_at = (src && src.smoothed_at) || keepStamp || null;
  return dst;
}
export const hasRecoveryState = (r) => !!r && !!(r.recovery_status || (Array.isArray(r.recovery_log) && r.recovery_log.length) || str(r.recovery_note).trim());

// The sync's keep rule for a thread the classifier now calls "not an issue"
// (a guest's "thanks!" flips it): an open recovery stays until someone
// closes it, and a CLOSED one stays for good, whatever its outcome.
// R114 fix (review recovery#1): a Smoothed close used to leave the store
// here — exactly the successful recoveries (a happy guest's "thanks" is what
// flips them), taking their append-only action log with them and dropping
// them from the Smoothed tab and "Smoothed this week". A close is a recorded
// human decision; only a never-touched, non-negative thread may leave.
export function keepForRecovery(r) {
  if (!r) return false;
  if (isClosed(r)) return true;
  return r.sentiment === 'negative' || r.recovery_status === 'open' || !!str(r.recovery_note).trim() || (Array.isArray(r.recovery_log) && r.recovery_log.length > 0);
}
// GET include rule for message tickets: anything someone has touched, plus
// every negative unresolved thread (a re-classified neutral ticket that is
// still OPEN on recovery stays visible).
export const inRecoveryList = (t) => !!t && (hasRecoveryState(t) || (t.sentiment === 'negative' && t.status !== 'resolved'));

// Union of two logs: dedupe by id, oldest first (stable for equal stamps).
export function mergeLogs(a, b) {
  const seen = new Set();
  const out = [];
  for (const e of [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]) {
    if (!e) continue;
    const k = e.id || `${e.at}|${e.kind}|${e.text}`;
    if (seen.has(k)) continue;
    seen.add(k); out.push({ ...e });
  }
  return out.map((e, i) => [e, i]).sort((x, y) => (parseTs(x[0].at) - parseTs(y[0].at)) || (x[1] - y[1])).map(p => p[0]);
}

// The recovery block a route answers with / a list row carries.
export function recoveryView(r) {
  return {
    ...pickRecovery(r),
    recovery_status: (r && r.recovery_status) || 'open',
    recovery_note: str(r && r.recovery_note),
    recovery_by: str(r && r.recovery_by),
    recovery_at: (r && r.recovery_at) || null,
    smoothed_at: (r && r.smoothed_at) || null,
    recovery_outcome: outcomeOf(r),
    recovery_state: stateOf(r),
    recovery_log: migrateLog(r),
  };
}

export function stateCounts(rows) {
  const c = { open: 0, smoothed: 0, partial: 0, unresolved: 0, blocked_owner: 0, closed: 0, all: 0 };
  for (const r of rows || []) { c[stateOf(r)]++; if (isClosed(r)) c.closed++; c.all++; }
  return c;
}
export function filterRows(rows, f) {
  const list = Array.isArray(rows) ? rows : [];
  if (!f || f === 'all') return list.slice();
  if (f === 'closed') return list.filter(isClosed);
  return list.filter(r => stateOf(r) === f);
}

// KPI band. "Smoothed this week" = outcome smoothed only (a blocked or
// unhappy close is not a win) — all-time until anything carries a
// smoothed_at stamp. "Not smoothed · 30 days" breaks the other outcomes out.
export function recoveryKpis(rows, nowMs, todayPT) {
  const now = Number(nowMs) || Date.now();
  const weekAgo = now - 7 * DAY, monthAgo = now - CLOSED_WINDOW_DAYS * DAY;
  const list = Array.isArray(rows) ? rows : [];
  const open = list.filter(r => !isClosed(r));
  const today = str(todayPT);
  const inHouse = today ? open.filter(r => r.check_in && r.check_out && str(r.check_in) <= today && today < str(r.check_out)).length : 0;
  const newWeek = list.filter(r => parseTs(r.last_message_at) >= weekAgo).length;
  const stamped = list.some(r => isClosed(r) && r.smoothed_at);
  const smoothed = list.filter(r => outcomeOf(r) === 'smoothed');
  const smoothedWeek = stamped ? smoothed.filter(r => parseTs(r.smoothed_at) >= weekAgo).length : smoothed.length;
  const byOutcome30 = { partial: 0, unresolved: 0, blocked_owner: 0 };
  for (const r of list) {
    const o = outcomeOf(r);
    if (o && o !== 'smoothed' && parseTs(closedAt(r)) >= monthAgo) byOutcome30[o]++;
  }
  return { open: open.length, inHouse, newWeek, smoothedWeek, stamped, notSmoothed30: byOutcome30.partial + byOutcome30.unresolved + byOutcome30.blocked_owner, byOutcome30 };
}

// After one row changes on the client, move the server's true counts / KPIs
// by exactly that row's contribution (both are per-row sums) instead of
// recomputing from a capped list.
export function bumpAggregates(agg, before, after, nowMs, todayPT) {
  const out = { counts: agg && agg.counts ? { ...agg.counts } : null, kpis: agg && agg.kpis ? { ...agg.kpis, byOutcome30: { ...(agg.kpis.byOutcome30 || {}) } } : null };
  if (out.counts) {
    const b = stateCounts([before]), a = stateCounts([after]);
    for (const k of Object.keys(a)) out.counts[k] = Math.max(0, (Number(out.counts[k]) || 0) + a[k] - b[k]);
  }
  if (out.kpis) {
    const b = recoveryKpis([before], nowMs, todayPT), a = recoveryKpis([after], nowMs, todayPT);
    for (const k of ['open', 'inHouse', 'newWeek', 'smoothedWeek', 'notSmoothed30']) out.kpis[k] = Math.max(0, (Number(out.kpis[k]) || 0) + a[k] - b[k]);
    for (const k of Object.keys(a.byOutcome30)) out.kpis.byOutcome30[k] = Math.max(0, (Number(out.kpis.byOutcome30[k]) || 0) + a.byOutcome30[k] - b.byOutcome30[k]);
    out.kpis.stamped = !!(out.kpis.stamped || a.stamped);
  }
  return out;
}

// owner_assignments { "<pms_listing_id>": "<owner_id>" } + owners [{id,name}]
// → lid → { owner_id, owner_name } (nulls when the listing has no owner yet).
export function ownerIndex(assignments, owners) {
  const map = assignments && typeof assignments === 'object' && !Array.isArray(assignments) ? assignments : {};
  const names = new Map((Array.isArray(owners) ? owners : []).filter(o => o && o.id != null).map(o => [String(o.id), str(o.name)]));
  return (lid) => {
    const oid = map[str(lid)];
    if (oid == null || oid === '') return { owner_id: null, owner_name: null };
    return { owner_id: str(oid), owner_name: names.get(str(oid)) || null };
  };
}

// GET /issues/guest-recovery list rules. rows carry _windowed (cases) and
// _window_at. Open rows are NEVER capped or windowed. Closed board cases
// leave the default view 30 days after closing; a filtered view (a closed
// outcome / closed / all, an owner, a listing, scope=all) bypasses that
// window. Closed rows are capped (newest message first) at 150, or 500 when
// filtered; `focus` (a row id or the ticket a case took over) is always
// included. counts / base are the TRUE totals for the owner/listing filter
// (open rows are never hidden and the closed tabs bypass the window).
export function listWindow(rows, q = {}, nowMs = Date.now()) {
  const state = STATE_QUERY.includes(q.state) ? q.state : '';
  const ownerId = q.owner_id != null && str(q.owner_id) ? str(q.owner_id) : '';
  const lids = q.listing_id != null && str(q.listing_id) ? str(q.listing_id).split(',').map(s => s.trim()).filter(Boolean) : [];
  const bypass = !!(ownerId || lids.length || q.scope === 'all' || (state && state !== 'open'));
  const base = (Array.isArray(rows) ? rows : []).filter(r => r && (!ownerId || str(r.owner_id) === ownerId) && (!lids.length || lids.includes(str(r.listing_id))));
  const start = Number(nowMs) - CLOSED_WINDOW_DAYS * DAY;
  const inWin = (r) => !isClosed(r) || !r._windowed || bypass || parseTs(r._window_at) > start;
  const pool = base.filter(inWin);
  const sel = state && state !== 'all' ? filterRows(pool, state) : pool;
  const newest = (a, b) => parseTs(b.last_message_at) - parseTs(a.last_message_at);
  const open = sel.filter(r => !isClosed(r));
  const closed = sel.filter(isClosed).sort(newest);
  const cap = bypass ? LIST_CAP_FILTERED : LIST_CAP;
  const kept = closed.slice(0, cap);
  const focus = str(q.focus);
  if (focus) {
    const f = base.find(r => str(r.id) === focus || str(r.ticket_id) === focus);
    if (f && !open.includes(f) && !kept.includes(f)) kept.push(f);
  }
  return {
    rows: [...open, ...kept].sort(newest),
    base,
    counts: stateCounts(base),
    window: { state: state || 'default', bypass, cap, closed_total: closed.length, closed_shown: Math.min(closed.length, cap), truncated: closed.length > cap },
  };
}

// The Blocked-by-owner tab: one group per owner, else per property (today
// owner_assignments covers almost no recovery listings).
export function groupForOwners(rows) {
  const groups = new Map();
  for (const r of rows || []) {
    if (!r) continue;
    const owned = r.owner_id != null && str(r.owner_id) !== '';
    const key = owned ? `owner:${r.owner_id}` : `listing:${str(r.listing_id) || '?'}`;
    let g = groups.get(key);
    if (!g) {
      g = { key, kind: owned ? 'owner' : 'property', owner_id: owned ? str(r.owner_id) : null, label: (owned ? str(r.owner_name) : '') || str(r.listing_name) || str(r.listing_id) || 'Unknown property', listings: [], rows: [] };
      groups.set(key, g);
    }
    if (r.listing_name && !g.listings.includes(str(r.listing_name))) g.listings.push(str(r.listing_name));
    g.rows.push(r);
  }
  const at = (r) => parseTs(closedAt(r)) || parseTs(r.last_message_at);
  for (const g of groups.values()) g.rows.sort((a, b) => at(b) - at(a));
  return [...groups.values()].sort((a, b) => (b.rows.length - a.rows.length) || a.label.localeCompare(b.label));
}

// The text a close / blocked entry carries (what we did / the owner's answer).
export function closeTextOf(r) {
  const log = migrateLog(r);
  for (let i = log.length - 1; i >= 0; i--) { const e = log[i]; if (e && e.kind === 'close' && str(e.text).trim()) return str(e.text).trim(); }
  for (let i = log.length - 1; i >= 0; i--) { const e = log[i]; if (e && str(e.text).trim()) return str(e.text).trim(); }
  return str(r && r.recovery_note).trim();
}

// ── owner-facing text: never the guest ───────────────────────────────────────
// R114 fix (review recovery#0) made names match TOKEN by token on a folded form
// (case, accents, ß/æ/ø…) so "Sarah's", "Jones'" and "Jean" (from "Jean-Luc")
// stop reaching the owner. R114 fix2 (rec2) keeps that and stops the scrubber
// garbling ordinary words — its plural / syllable guesses turned "scuff marks"
// into "scuff the guest's", "won't" into "the guest't" and "he" (another
// guest's surname) into "the guest". The rules now:
//   • a name token matches as a WHOLE word (any case / accents) plus its
//     possessive ('s, ’s, s'), and a whole name run together ("SarahJones",
//     "OBrien", "Jean-Luc") as one word;
//   • a family plural / missing-apostrophe possessive ("the Lees", "Sarahs",
//     "the Joneses") only for a token that is NOT an everyday word — so a
//     guest called Mark, Bill, Rose or Fan never turns "marks", "bills",
//     "roses" or "fans" into a name. R114 fix3: a name shorter than 4 letters
//     (Lo, Cat, Jet, Dot, Lee, Kim) takes a plural ONLY in the family form
//     "the Lees" / "the Kims" — never "2 cats", "tub jets", "Trader Joes" —
//     and not when a capitalised word follows ("the Los Gatos home"), nor as
//     an all-caps acronym in mixed-case text ("called the EMS");
//   • a hyphen / apostrophe piece of a name ("Jean" of Jean-Luc, "Brien" of
//     O'Brien) counts on its own only when it has 3+ letters and is not an
//     everyday word ("See" of Wing-See, "In" of Hye-In, "An" of Ji-An don't);
//   • a name token that IS an everyday word ("He", "Will", "Still", "See",
//     "King", "Rose") matches only where it is written as a name: capitalised
//     mid-sentence, after Mr / Mrs / Dr…, or right beside another matched part
//     of the name ("He Wei", "Will Smith"). In the guest's OWN row a content
//     word name (Rose, King, Mark, Brown…) also matches capitalised at a
//     sentence start and written in ALL CAPS ("MARK CALLED"). Two parts of the
//     same guest's name side by side are the name in any case ("FRANK HILL",
//     "rose lam") unless the part is a function word (he / will / see).
//     R114 fix3: a following number never protects a name ("Refunded Sarah
//     Brown 75", "Called Grace 3 times") — only a month / weekday read as a
//     date is left alone ("June 3", "Sun 10/12", "June 2026"; but "Called
//     June 3 times" is the guest);
//   • contractions are never touched: won't / can't / don't keep their
//     negation whatever the guest is called, and he'll / it's stay pronouns.
const APOS = "'\u2019\u02bc\u2018`\u00b4";        // ' ’ ʼ ‘ ` ´
const JOINERS = `\\-\u2010\u2011${APOS}`;            // - ‐ ‑ + the apostrophes (regex-class-safe)
const FOLD_MAP = { 'ß': 'ss', 'æ': 'ae', 'œ': 'oe', 'ø': 'o', 'ł': 'l', 'đ': 'd', 'ð': 'd', 'þ': 'th', 'ı': 'i', 'ħ': 'h' };
// "ZOË" → "zoe", "Søren" → "soren", "Straße" → "strasse"
export function foldName(s) {
  return str(s).toLowerCase().normalize('NFKD').replace(/\p{M}+/gu, '').replace(/[ßæœøłđðþıħ]/g, (c) => FOLD_MAP[c] || c);
}
// words in a guest_name that are never the person ("John and Mary Smith",
// "The Lee Family", "Mrs. Jones") — scrubbing them would wreck the sentence
const NAME_STOP = new Set(['the', 'and', 'or', 'of', 'guest', 'guests', 'family', 'mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'jr', 'sr', 'ii', 'iii', 'iv']);
const HONORIFIC = new Set(['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'sir', 'madam', 'mme', 'mlle', 'sra', 'srta', 'herr', 'frau']);
const words = (s) => new Set(s.trim().split(/\s+/));
// Everyday words: function words + the words our notes are made of. A name
// token that is one of these is a name only where it is WRITTEN as one.
const EVERYDAY = words(`
  a am an and any are as at be been being both but by can cannot could did do does done down each either for from
  get gets got had has have having he her here hers him his how i if in into is it its just let lets like many may
  me might mine more most much must my near need no nor not now of off oh ok okay on once one only onto or other
  our ours out over own per please said same say says see seen shall she should since so some still such than
  thank thanks that the their theirs them then there these they this those though thus till to too two up upon us
  very via was way we well were what when where which while who whom whose why will with within without would yes
  yet you your yours ye yo hi hey ha ah uh um
  about above after again against ago all along also already always among another around away back before behind
  below beside between beyond during early else even ever every few first full good great half last late later
  left less little long made make never new next night day week old right since soon sure today told tomorrow
  tonight under until yesterday min max mins hrs mos am pm due main master queen twin double single super
  maam oclock yall mon tue tues wed thu thur thurs fri sat sun jan feb mar apr jun jul aug sep sept oct nov dec
  los las
`);
// Content words that are also names (Rose, King, Mark, Bill, Fan, Wing…) plus
// the plurals a note is full of. Never a plural / standalone-piece match.
const COMMON = words(`
  amber angel ant ants april art arts ash august autumn bang bangs bank banks bath baths bay beach bean beans bear
  bee bees bell bells berry bill bills bin bins birch black blind blinds blue bond brass brook brooks brown buck bud
  burn burns bush bushes cable cam cams carol cash chase cherry chip chips clay cliff coal cole cook copper coral cord
  cords cross crystal dale dawn dean dent dents diamond ding dings dove drake duke dusty earl east eve fair faith fan
  fans fern field fields finch fish flood floods ford forest fox frank frost gale garden gate gates glass glen gold
  golden grace grant gray green grey grove guy hall hart hawk hazel heath heather hill hills holly honey hook hooks
  hope house hung hunt iris iron ivy jack jacks jade jay jeans jewel joy judge june key keys king kit kits lake lamb
  lance lane light lights lily lock locke locks love major man march mark marks marsh mat mats meadow mercy miles mill mills moon
  moss nail nails nick nicks nickel north oak olive page pages pan pans park parks pat pearl penny pepper pin pins
  pine pink plum pool pools power powers price prince rain ray rays reed rice rich ring rings rob robin rock rocks
  rocky rod rods rose roses ruby rush rusty sage salt sand sandy sharp shade shades short silver sky small snow song
  south spark sparks spring stone stones storm strong sugar summer sun sunny swan swift tan temple tin van vans wall
  walls ward water waters watt watts wells west white wild wing winter wolf wood woods wren young
  aces bus cats docs dots gas jets pegs tabs
`);
// a month / weekday after "in" / "on" / "since"… is the date, not a guest
// called June / May / Sun ("since June", "on Sun")
const DATE_WORD = words(`jan feb mar apr may jun jul aug sep sept oct nov dec january february march april june july
  august september october november december mon tue tues wed thu thur thurs fri sat sun`);
const WEEKDAY = words('monday tuesday wednesday thursday friday saturday sunday');
const DATE_PREP = words('in on since until till by from last next this early mid late of through thru before after during');
// "June 3", "June 3rd", "Sun 10/12", "June 2026" — a day / date / year after a
// month or weekday. "June 3 times", "Mark 2x", "Brown 75" are a count or an
// amount after a NAME, never a date.
const COUNT_UNIT = /^\s*(?:x|times?|nights?|nts?|guests?|people|persons?|ppl|adults?|kids?|children|pets?|dogs?|days?|hours?|hrs?|mins?|minutes?|weeks?|wks?|months?|dollars?|bucks|usd|calls?|texts?|messages?|msgs?|emails?|beds?|rooms?|towels?|stars?|percent|pct|%)(?![\p{L}\p{N}])/iu;
function dateAfter(rest) {
  if (/^\s*\d{1,2}[/.-]\d/.test(rest)) return true;                       // 10/12 · 3-4
  if (/^\s*(?:19|20)\d\d(?![\p{L}\p{N}])/u.test(rest)) return true;       // a year
  const m = rest.match(/^\s*(\d{1,2})(?:st|nd|rd|th)?(?![\p{L}\p{N}])/iu);
  return !!m && Number(m[1]) >= 1 && Number(m[1]) <= 31 && !COUNT_UNIT.test(rest.slice(m[0].length));
}
// scripts written without spaces: a name there is matched as a substring
const NO_SPACE_SCRIPT = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}]/u;
const hasLetter = (s) => /\p{L}/u.test(s);
const keyOf = (s) => foldName(s).replace(/[^\p{L}\p{N}]+/gu, '');
const everyday = (k) => EVERYDAY.has(k) || COMMON.has(k);
// how a key matches: STRONG anywhere in any case; COMMON (a content word that
// is a whole name part) capitalised; WEAK only where written as a name
const STRONG = 3, CONTENT = 2, WEAK = 1;

// Every folded token of every name with its tier: each whole part ("sarah",
// "obrien", "jeanluc"), each 3+ letter hyphen / apostrophe piece ("jean",
// "luc", "brien") and the full name run together ("sarahjones").
// keys = all of them (back-compat); tiers = key → STRONG | CONTENT | WEAK;
// pairs = "a|b" for two whole parts side by side in one name, either order
// ("frank|hill", "hill|frank") — written together they are the person.
export function guestNameKeys(names) {
  const tiers = new Map(), substr = new Set(), pairs = new Set();
  const put = (k, t) => { if (k.length >= 2 && hasLetter(k) && !NAME_STOP.has(k) && t > (tiers.get(k) || 0)) tiers.set(k, t); };
  for (const nm of (Array.isArray(names) ? names : [names])) {
    const all = [], wholes = [];
    let parts = 0;
    for (const part of str(nm).normalize('NFC').split(new RegExp(`[^\\p{L}\\p{M}\\p{N}${JOINERS}]+`, 'u'))) {
      if (!part) continue;
      if (NO_SPACE_SCRIPT.test(part) && part.length >= 2) substr.add(part.normalize('NFKC'));
      const segs = foldName(part).split(/[^\p{L}\p{N}]+/u).filter(Boolean);
      if (!segs.length) continue;
      const whole = segs.join('');
      if (NAME_STOP.has(whole)) continue;
      put(whole, EVERYDAY.has(whole) ? WEAK : COMMON.has(whole) ? CONTENT : STRONG);
      // a piece: 3+ letters and not an everyday word = STRONG; "See" / "In" /
      // "An" (everyday) only where written as a name; "Wing" / "Ji" capitalised
      if (segs.length > 1) for (const g of segs) put(g, EVERYDAY.has(g) ? WEAK : g.length >= 3 && !COMMON.has(g) ? STRONG : CONTENT);
      all.push(...segs); parts++;
      if (whole.length >= 2) wholes.push(whole);
    }
    for (let i = 1; i < wholes.length; i++) { pairs.add(`${wholes[i - 1]}|${wholes[i]}`); pairs.add(`${wholes[i]}|${wholes[i - 1]}`); }
    const full = all.join('');
    if (parts > 1 || all.length > 1) put(full, STRONG);   // "SarahJones", "HeWei" — never an everyday word
    if (NO_SPACE_SCRIPT.test(str(nm))) { const run = str(nm).normalize('NFKC').replace(/\s+/g, ''); if (run.length >= 2) substr.add(run); }
  }
  return { keys: new Set(tiers.keys()), tiers, pairs, substr: [...substr].sort((a, b) => b.length - a.length) };
}

const isTitle = (w) => { const c = w.charAt(0); return c !== c.toLowerCase() && (w.length === 1 || w.slice(1) !== w.slice(1).toUpperCase()); };
const isUpper = (w) => w === w.toUpperCase() && w !== w.toLowerCase();
const isLower = (w) => w === w.toLowerCase() && w !== w.toUpperCase();
const SENT_END = /[.!?…:;•·|—–(\[{"“«\n\r]/u;
const CONTRACTION = new RegExp(`^([\\p{L}\\p{M}]+)([${APOS}])(t|ll|re|ve|d|m)$`, 'iu');
const POSS = new RegExp(`^(.*?[\\p{L}\\p{M}])([${APOS}])[sS]$`, 'u');
const PIECE_SPLIT = new RegExp(`([${JOINERS}])`, 'u');
const WORD = new RegExp(`[\\p{L}\\p{M}]+(?:[${JOINERS}][\\p{L}\\p{M}]+)*`, 'gu');
const APOS_CH = new RegExp(`^[${APOS}]$`, 'u');

// Owner-facing text never carries the guest: drop the name(s) (rules above),
// emails and phone-number-looking runs from free text. guestName = one
// guest_name or a list — the row's OWN guest(s); others = the other guests of
// the group (their everyday-word names match only where written as a name).
export function scrubGuest(text, guestName, others = []) {
  let s = str(text).normalize('NFC');
  s = s.replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, '[email]');
  // a phone = 10+ digits in one run of digits / spaces / ().- (a "2026-10-09"
  // date has 8 and survives), or a bare 7-digit local "555-0123"
  s = s.replace(/\+?\(?\d[\d\s().-]{7,}\d/g, (m) => (m.replace(/\D/g, '').length >= 10 ? '[phone]' : m));
  s = s.replace(/(^|[^\d-])\d{3}[-.]\d{4}(?![\d-])/g, '$1[phone]');
  // an ALL-CAPS note has no case signal: "THE LEES" there is the family, while
  // an all-caps word in mixed-case text is an acronym ("the EMS", "AC")
  const capsNote = !/\p{Ll}/u.test(s.replace(/\[(?:email|phone)\]/g, ''));
  const own = guestNameKeys(guestName);
  const oth = guestNameKeys(Array.isArray(others) ? others : [others]);
  const pairs = new Set([...own.pairs, ...oth.pairs]);
  // another stay's everyday-word name (Still, King, Rose…) never reaches into
  // this row's ordinary words — it matches only where written as a name
  const tierOf = (k) => { const o = oth.tiers.get(k) || 0; return Math.max(own.tiers.get(k) || 0, o === CONTENT ? WEAK : o); };
  const substr = [...new Set([...own.substr, ...oth.substr])].sort((a, b) => b.length - a.length);
  if (own.tiers.size || oth.tiers.size || substr.length) {
    s = s.normalize('NFKC');
    for (const k of substr) s = s.split(k).join(' the guest ');
    const toks = [...s.matchAll(WORD)].map(m => ({ w: m[0], i: m.index, end: m.index + m[0].length, out: null, cand: 0 }));
    const gap = (a, b) => s.slice(a.end, b.i);
    const sentenceStart = (t, prev) => {
      let j = t.i - 1;
      while (j >= 0 && /\s/.test(s[j])) j--;
      if (j < 0) return true;
      const ch = s[j];
      if (ch === '.' && prev && prev.end === j && HONORIFIC.has(foldName(prev.w))) return false;   // "Mr. He"
      if (SENT_END.test(ch)) return true;
      if ((ch === '-' || APOS_CH.test(ch)) && (j === 0 || /\s/.test(s[j - 1]))) return true;   // "- He …", "'Will' …"
      return false;
    };
    const afterHonorific = (t, prev) => !!prev && HONORIFIC.has(foldName(prev.w)) && /^\.?\s+$/.test(gap(prev, t));
    // "June 3", "Sun 10/12", "since June" — the date, never a name. Only a
    // month / weekday: a number after any other name part is a count or an
    // amount ("Refunded Sarah Brown 75", "Called Grace 3 times") — R114 fix3
    const dateUse = (t, prev, k) => DATE_WORD.has(k) && (dateAfter(s.slice(t.end)) || (!!prev && DATE_PREP.has(foldName(prev.w)) && /^\s+$/.test(gap(prev, t))));
    for (let n = 0; n < toks.length; n++) {
      const t = toks[n], prev = toks[n - 1];
      let w = t.w;
      // a possessive s' ("Jones' kids", "the Joneses' car") — a closing quote
      // after any other word ("'Sarah' wrote") is left alone
      let tail = '';
      if (/[sS]$/.test(w) && APOS_CH.test(s[t.end] || '') && !/[\p{L}\p{M}]/u.test(s[t.end + 1] || '')) { t.end += 1; tail = "'s"; }
      const ct = !tail && w.match(CONTRACTION);
      if (ct) {                                                   // won't · can't · he'll · Sarah'll
        if (ct[3].toLowerCase() !== 't' && tierOf(keyOf(ct[1])) === STRONG) t.out = `the guest${ct[2]}${ct[3]}`;
        continue;
      }
      const pm = !tail && w.match(POSS);
      if (pm) { w = pm[1]; tail = "'s"; }                        // Sarah's · O'Brien's · Jones’s
      const k = keyOf(w);
      t.k = k;
      const tier = tierOf(k);
      const as = `the guest${tail}`;
      if (tier === STRONG) { t.out = as; continue; }
      if (tier) {
        if (dateUse(t, prev, k)) continue;
        // the row's own content-word name (Mark, Grace, Hill…) written in ALL
        // CAPS is written as a name too ("MARK CALLED", "FRANK HILL") — R114 fix3
        const ownContent = own.tiers.get(k) === CONTENT;
        const ctx = (isTitle(w) || (ownContent && isUpper(w))) && (afterHonorific(t, prev) || !sentenceStart(t, prev) || ownContent);
        if (ctx) t.out = as; else { t.cand = tier; t.as = as; t.word = w; }
        continue;
      }
      if (everyday(k)) continue;                                  // ma'am · o'clock · jeans · marks
      // a family plural / missing-apostrophe possessive of a name that is not
      // an everyday word: "the Lees", "the Kims", "Sarahs dog", "the Joneses"
      // ("the Lees' car" → "the guests' car")
      if (!pm) {
        const stem = k.endsWith('es') && /(s|x|z|ch|sh)$/.test(k.slice(0, -2)) && tierOf(k.slice(0, -2)) === STRONG ? k.slice(0, -2)
          : (k.endsWith('s') && !k.endsWith('ss') && tierOf(k.slice(0, -1)) === STRONG ? k.slice(0, -1) : '');
        const family = !!stem && !!prev && foldName(prev.w) === 'the' && /^\s+$/.test(gap(prev, t));
        // R114 fix3: a name under 4 letters (Lo, Cat, Jet, Dot, Lee, Kim)
        // takes a plural only as the family — "the Lees" / "the Kims" — never
        // "2 cats" / "tub jets" / "Trader Joes", nor "the Los Gatos home" (a
        // capitalised word follows; an acronym like "AC" or a month / weekday
        // after it doesn't count), nor an all-caps acronym in mixed-case text
        // ("called the EMS")
        const next = s.slice(t.end).match(/^\s+([\p{L}\p{M}]+)/u);
        const placeNext = !!next && next[1].length > 1 && isTitle(next[1]) && !DATE_WORD.has(keyOf(next[1])) && !WEEKDAY.has(keyOf(next[1]));
        const shortOk = family && (isUpper(w) ? (capsNote || stem.length >= 3) : (isLower(w) || !placeNext));
        if (stem && (stem.length >= 4 || shortOk)) {
          t.out = family ? (tail ? "the guests'" : 'the guests') : "the guest's";
          continue;
        }
      }
      // one piece of a joined word: "Jean" in "Jean-Marc", "Ramos" in "Ramos-Barrera"
      const bits = w.split(PIECE_SPLIT);
      if (bits.length > 1) {
        let hit = false;
        const out = bits.map((b, i) => {
          if (i % 2) return b;
          if (tierOf(keyOf(b)) === STRONG) { hit = true; return 'the guest'; }
          return b;
        });
        if (hit) t.out = out.join('') + tail;
      }
    }
    // an everyday-word part right beside a matched part is the name too
    // ("He Wei", "Will Smith", "rose kim") — same case shape as written.
    // R114 fix3: two parts of ONE guest's name side by side are that guest in
    // any case — "FRANK HILL", "refunded grace park", another stay's "rose
    // lam" — unless the part is a function word (he / will / see), which still
    // needs the case shape ("Li he said" keeps "he"). fix3 verify: the
    // NEIGHBOUR must not be an everyday word either — guest "Will Park" must
    // leave "guest will park on street" alone.
    for (let changed = true; changed;) {
      changed = false;
      toks.forEach((t, n) => {
        if (!t.cand || t.out != null) return;
        for (const o of [toks[n - 1], toks[n + 1]]) {
          if (!o) continue;
          const left = o === toks[n - 1];
          if (!/^\s+$/.test(left ? gap(o, t) : gap(t, o))) continue;
          const pair = (o.out != null || o.cand) && !!o.k && !EVERYDAY.has(t.k) && !EVERYDAY.has(o.k) && pairs.has(left ? `${o.k}|${t.k}` : `${t.k}|${o.k}`);
          if (pair || (o.out != null && (isTitle(t.word) || (isUpper(t.word) && isUpper(o.w)) || (t.cand === CONTENT && isLower(t.word) && isLower(o.w))))) { t.out = t.as; changed = true; break; }
        }
      });
    }
    let built = '', at = 0;
    for (const t of toks) { if (t.out == null) continue; built += s.slice(at, t.i) + t.out; at = t.end; }
    s = built + s.slice(at);
  }
  s = s.replace(/\s+/g, ' ').trim();
  // "Sarah Jones" / "Jones, Sarah" / "the guest Sarah" → one "the guest"
  s = s.replace(/\b(the)\s+the (guest)/gi, '$1 $2');
  while (/the guest(?: ?, ?| )the guest/.test(s)) s = s.replace(/the guest(?: ?, ?| )the guest/g, 'the guest');
  return s;
}

const fmtDay = (s) => {
  const ms = parseTs(s);
  return ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '—';
};

// Plain text for the owner call: date · property · what the guest asked ·
// what we did / the owner's answer. No guest names, phones or emails.
// R114 fix (review recovery#0): built only from fields we write — the date,
// the property, the one-line issue summary (the classifier's / the board
// title — never the guest's own message: the excerpt is their words, with
// their signature) and our close note — and every free-text part is scrubbed
// of EVERY guest in the group (a note may name another stay's guest).
// R114 fix2: the row's own guest at full strength, the others' everyday-word
// names only where written as a name — another stay's guest surnamed He must
// not turn "Owner said he will not approve" into "the guest will not approve".
export function ownerCallText(group) {
  const g = group || { rows: [] };
  const rows = Array.isArray(g.rows) ? g.rows : [];
  const names = rows.map(r => r && r.guest_name).filter(Boolean);
  const head = `${str(g.label) || 'Owner'} — guest requests the owner declined (${rows.length})`;
  const lines = rows.map(r => {
    const own = str(r.guest_name);
    const others = names.filter(n => n !== own);
    const ask = scrubGuest(str(r.summary).trim() || 'a guest request', own, others);
    const ans = scrubGuest(closeTextOf(r), own, others);
    return `• ${fmtDay(r.last_message_at || r.check_in)} · ${str(r.listing_name) || str(r.listing_id)} — Guest asked: ${ask}${ans ? ` — What we did / owner's answer: ${ans}` : ''}`;
  });
  return [head, ...lines].join('\n');
}

// " · blocked by owner" etc. for a routed Issues-board chip.
export function outcomeSuffix(rt) {
  if (!rt) return '';
  if (rt.recovery_status === 'missing') return ' · removed';
  if (rt.recovery_status !== 'smoothed') return '';
  const o = OUTCOMES.includes(rt.recovery_outcome) ? rt.recovery_outcome : 'smoothed';
  return ` · ${OUTCOME_LABEL[o].toLowerCase()}`;
}
